/**
 * Utility functions for automatic mathematical notation conversion
 * Converts typed patterns like "23^e" to proper mathematical notation
 */

/**
 * Convert common mathematical patterns to LaTeX format for visual rendering
 * @param {string} text - The input text to convert
 * @returns {string} - The converted text in LaTeX format
 */
export const autoConvertMathNotation = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  let converted = text;
  
  // Greek letters conversions (most important ones first)
  const greekLetters = {
    'pi': '\\pi',
    'Pi': '\\Pi', 
    'PI': '\\Pi',
    'alpha': '\\alpha',
    'Alpha': '\\Alpha',
    'beta': '\\beta',
    'Beta': '\\Beta',
    'gamma': '\\gamma',
    'Gamma': '\\Gamma',
    'delta': '\\delta',
    'Delta': '\\Delta',
    'epsilon': '\\varepsilon',
    'Epsilon': '\\varepsilon',
    'theta': '\\theta',
    'Theta': '\\Theta',
    'lambda': '\\lambda',
    'Lambda': '\\Lambda',
    'mu': '\\mu',
    'Mu': '\\mu',
    'sigma': '\\sigma',
    'Sigma': '\\Sigma',
    'phi': '\\phi',
    'Phi': '\\Phi',
    'omega': '\\omega',
    'Omega': '\\Omega'
  };
  
  // Convert Greek letter names to LaTeX symbols (word boundaries to avoid partial matches)
  // But avoid converting if already in LaTeX format (preceded by backslash)
  Object.entries(greekLetters).forEach(([name, symbol]) => {
    // Create regex that matches the name but not when preceded by a backslash
    const regex = new RegExp(`(^|[^\\\\])\\b${name}\\b`, 'g');
    converted = converted.replace(regex, (match, prefix) => prefix + symbol);
  });
  
  // Integral notations (process before superscripts to handle int_a^b patterns)
  // First, convert unicode integral symbol to text 'int' to normalize input
  converted = converted.replace(/∫/g, 'int');
  
  // Definite integral with brackets: int[a,b] f(x) dx
  if (!converted.includes('\\int_')) {
    converted = converted.replace(/\bint\[([^,\]]+),([^\]]+)\]\s+([^d]+?)\s*d([a-zA-Z])\b/g, '\\int_{$1}^{$2} $3 \\, d$4');
  }
  
  // Indefinite integral: int f(x) dx (do this after definite integrals)
  // Only convert if not already in LaTeX form (avoid \\int -> \\\\int)
  if (!converted.includes('\\int')) {
    converted = converted.replace(/\bint\s+([^d]+?)\s+d([a-zA-Z])\b/g, '\\int $1 \\, d$2');
  }
  
  // Superscript patterns - handle cases like "23^e", "x^2", "a^n", "123^123"
  // Convert patterns like "23^e" to "23^{e}" for proper LaTeX superscript rendering
  // This matches number/letter followed by ^ followed by any alphanumeric sequence
  converted = converted.replace(/([a-zA-Z0-9\\]+)\^([a-zA-Z0-9\\]+)(?![a-zA-Z0-9}])/g, '$1^{$2}');
  
  // Handle more complex superscript patterns with parentheses
  converted = converted.replace(/([a-zA-Z0-9\\]+)\^\(([^)]+)\)/g, '$1^{$2}');
  
  // Handle infinity (including negative infinity)
  converted = converted.replace(/\b-infinity\b/gi, '-\\infty');
  converted = converted.replace(/\binfinity\b/gi, '\\infty');
  
  // Mathematical constants
  converted = converted.replace(/\beuler\b/gi, 'e'); // Euler's number
  
  // Common function notations - ensure proper LaTeX formatting (with double-conversion protection)
  if (!converted.includes('\\ln ')) {
    // Handle ln with space: "ln x" -> "\ln x"
    converted = converted.replace(/\bln\s+([a-zA-Z0-9\\()]+)/g, '\\ln $1');
  }
  if (!converted.includes('\\ln(')) {
    // Handle ln with parentheses: "ln(x)" -> "\ln(x)"
    converted = converted.replace(/\bln\s*\(/g, '\\ln(');
  }
  
  // Handle logarithms with base: "log_2 x" or "log_10(x)"
  if (!converted.includes('\\log_')) {
    converted = converted.replace(/\blog_([a-zA-Z0-9]+)\s*\(/g, '\\log_{$1}(');
    converted = converted.replace(/\blog_([a-zA-Z0-9]+)\s+([a-zA-Z0-9\\()]+)/g, '\\log_{$1} $2');
  }
  if (!converted.includes('\\log ')) {
    // Handle log with space: "log x" -> "\log x"
    converted = converted.replace(/\blog\s+([a-zA-Z0-9\\()]+)/g, '\\log $1');
  }
  if (!converted.includes('\\log(')) {
    // Handle log with parentheses: "log(x)" -> "\log(x)"
    converted = converted.replace(/\blog\s*\(/g, '\\log(');
  }
  if (!converted.includes('\\sin(')) {
    converted = converted.replace(/\bsin\s*\(/g, '\\sin(');
  }
  if (!converted.includes('\\cos(')) {
    converted = converted.replace(/\bcos\s*\(/g, '\\cos(');
  }
  if (!converted.includes('\\tan(')) {
    converted = converted.replace(/\btan\s*\(/g, '\\tan(');
  }
  
  // Inverse trigonometric functions (with double-conversion protection)
  if (!converted.includes('\\arcsin(')) {
    converted = converted.replace(/\barcsin\s*\(/g, '\\arcsin(');
    converted = converted.replace(/\bsin\^(-1)\s*\(/g, '\\arcsin(');
  }
  if (!converted.includes('\\arccos(')) {
    converted = converted.replace(/\barccos\s*\(/g, '\\arccos(');
    converted = converted.replace(/\bcos\^(-1)\s*\(/g, '\\arccos(');
  }
  if (!converted.includes('\\arctan(')) {
    converted = converted.replace(/\barctan\s*\(/g, '\\arctan(');
    converted = converted.replace(/\btan\^(-1)\s*\(/g, '\\arctan(');
  }
  
  // Hyperbolic functions (with double-conversion protection)
  if (!converted.includes('\\sinh(')) {
    converted = converted.replace(/\bsinh\s*\(/g, '\\sinh(');
  }
  if (!converted.includes('\\cosh(')) {
    converted = converted.replace(/\bcosh\s*\(/g, '\\cosh(');
  }
  if (!converted.includes('\\tanh(')) {
    converted = converted.replace(/\btanh\s*\(/g, '\\tanh(');
  }
  
  // Inverse hyperbolic functions (with double-conversion protection)
  if (!converted.includes('\\text{arcsinh}(')) {
    converted = converted.replace(/\barcsinh\s*\(/g, '\\text{arcsinh}(');
    converted = converted.replace(/\bsinh\^(-1)\s*\(/g, '\\text{sinh}^{-1}(');
  }
  if (!converted.includes('\\text{arccosh}(')) {
    converted = converted.replace(/\barccosh\s*\(/g, '\\text{arccosh}(');
    converted = converted.replace(/\bcosh\^(-1)\s*\(/g, '\\text{cosh}^{-1}(');
  }
  if (!converted.includes('\\text{arctanh}(')) {
    converted = converted.replace(/\barctanh\s*\(/g, '\\text{arctanh}(');
    converted = converted.replace(/\btanh\^(-1)\s*\(/g, '\\text{tanh}^{-1}(');
  }
  
  // Inequality symbols
  converted = converted.replace(/<=|≤/g, '\\leq');
  converted = converted.replace(/>=|≥/g, '\\geq');
  converted = converted.replace(/!=/g, '\\neq');
  
  // Plus/minus
  converted = converted.replace(/\+-/g, '\\pm');
  
  // Handle sqrt notation
  converted = converted.replace(/\bsqrt\s*\(/g, '\\sqrt{');
  // Close the sqrt if there's a matching closing parenthesis
  converted = converted.replace(/\\sqrt\{([^}]*)\)/g, '\\sqrt{$1}');
  
  // Derivative notations
  // dy/dx pattern - convert to proper fraction notation
  converted = converted.replace(/\bd([a-zA-Z])\s*\/\s*d([a-zA-Z])\b/g, '\\frac{d$1}{d$2}');
  
  // More general derivative patterns like d/dx, d²/dx², etc.
  converted = converted.replace(/\bd\s*\/\s*d([a-zA-Z])\b/g, '\\frac{d}{d$1}');
  converted = converted.replace(/\bd²\s*\/\s*d([a-zA-Z])²\b/g, '\\frac{d^2}{d$1^2}');
  converted = converted.replace(/\bd2\s*\/\s*d([a-zA-Z])2\b/g, '\\frac{d^2}{d$1^2}');
  converted = converted.replace(/\bd\^(\d+)\s*\/\s*d([a-zA-Z])\^(\d+)\b/g, '\\frac{d^{$1}}{d$2^{$3}}');
  
  // Partial derivatives (unicode symbols)
  converted = converted.replace(/∂([a-zA-Z])\s*\/\s*∂([a-zA-Z])/g, '\\frac{\\partial $1}{\\partial $2}');
  converted = converted.replace(/∂\s*\/\s*∂([a-zA-Z])/g, '\\frac{\\partial}{\\partial $1}');
  
  // Partial derivatives (text form)
  converted = converted.replace(/\bpartial([a-zA-Z])\s*\/\s*partial([a-zA-Z])\b/g, '\\frac{\\partial $1}{\\partial $2}');
  converted = converted.replace(/\bpartial\s*\/\s*partial([a-zA-Z])\b/g, '\\frac{\\partial}{\\partial $1}');
  
  // Double and triple integrals
  converted = converted.replace(/\biint\b/g, '\\iint');
  converted = converted.replace(/\biiint\b/g, '\\iiint');
  converted = converted.replace(/\b∬\b/g, '\\iint');
  converted = converted.replace(/\b∭\b/g, '\\iiint');
  
  return converted;
};

/**
 * Get cursor position adjustment after conversion
 * @param {string} originalText - Original text before conversion
 * @param {string} convertedText - Text after conversion
 * @param {number} originalCursor - Original cursor position
 * @returns {number} - Adjusted cursor position
 */
export const adjustCursorPosition = (originalText, convertedText, originalCursor) => {
  if (originalText === convertedText) return originalCursor;
  
  // For simple conversions, try to maintain relative position
  const lengthDiff = convertedText.length - originalText.length;
  
  // If text got shorter (like "pi" -> "π"), adjust cursor back
  if (lengthDiff < 0) {
    return Math.max(0, originalCursor + lengthDiff);
  }
  
  // If text got longer (like "a^e" -> "a^{e}"), cursor likely stays in same relative position
  if (lengthDiff > 0) {
    // Simple heuristic: if cursor was at end, keep it at end
    if (originalCursor >= originalText.length) {
      return convertedText.length;
    }
    // Otherwise, try to keep it at roughly the same position
    return originalCursor;
  }
  
  return originalCursor;
};

/**
 * Apply conversion with debouncing to prevent excessive updates
 * @param {string} text - Input text
 * @param {number} cursorPos - Current cursor position
 * @param {function} callback - Callback to execute with converted text and new cursor position
 * @param {number} delay - Debounce delay in milliseconds (default: 500)
 */
export const debouncedConversion = (() => {
  let timeoutId = null;
  
  return (text, cursorPos, callback, delay = 500) => {
    clearTimeout(timeoutId);
    
    timeoutId = setTimeout(() => {
      const converted = autoConvertMathNotation(text);
      if (converted !== text) {
        const newCursorPos = adjustCursorPosition(text, converted, cursorPos);
        callback(converted, newCursorPos);
      }
    }, delay);
  };
})();

/**
 * Check if conversion should be triggered based on typed characters
 * @param {string} text - Current text
 * @param {string} previousText - Previous text
 * @returns {boolean} - Whether to trigger conversion
 */
export const shouldTriggerConversion = (text, previousText) => {
  if (!text || !previousText) return false;
  
  // Trigger conversion on space, or when user finishes typing a word
  const lastChar = text[text.length - 1];
  const triggerChars = [' ', '\n', '\t', ')', '}', ']', '+', '-', '*', '/', '='];
  
  if (triggerChars.includes(lastChar)) return true;
  
  // Trigger when user types specific patterns that need immediate conversion
  const immediatePatterns = [
    /\d+\^[a-zA-Z0-9]+$/,    // patterns like "23^e", "x^2", "123^123" 
    /[a-zA-Z]+\^[a-zA-Z\d]+$/, // patterns like "sin^2", "a^n"
    /pi$/i,              // ends with "pi"
    /alpha$/i,           // ends with "alpha"
    /beta$/i,            // ends with "beta"
    /gamma$/i,           // ends with "gamma"
    /delta$/i,           // ends with "delta"
    /theta$/i,           // ends with "theta"
    /lambda$/i,          // ends with "lambda"
    /sigma$/i,           // ends with "sigma"
    /omega$/i,           // ends with "omega"
    /infinity$/i,        // ends with "infinity"
    /-infinity$/i,       // ends with "-infinity"
    // Logarithmic functions
    /\bln\s+[a-zA-Z0-9]+$/,  // patterns like "ln x"
    /\blog\s+[a-zA-Z0-9]+$/,  // patterns like "log x"
    /\blog_[a-zA-Z0-9]+\s+[a-zA-Z0-9]+$/,  // patterns like "log_2 x"
    // Trigonometric and hyperbolic functions
    /arcsin$/i,          // ends with "arcsin"
    /arccos$/i,          // ends with "arccos"
    /arctan$/i,          // ends with "arctan"
    /sinh$/i,            // ends with "sinh"
    /cosh$/i,            // ends with "cosh"
    /tanh$/i,            // ends with "tanh"
    /arcsinh$/i,         // ends with "arcsinh"
    /arccosh$/i,         // ends with "arccosh"
    /arctanh$/i,         // ends with "arctanh"
    /sin\^(-1)$/i,       // ends with "sin^(-1)"
    /cos\^(-1)$/i,       // ends with "cos^(-1)"
    /tan\^(-1)$/i,       // ends with "tan^(-1)"
    /sinh\^(-1)$/i,      // ends with "sinh^(-1)"
    /cosh\^(-1)$/i,      // ends with "cosh^(-1)"
    /tanh\^(-1)$/i,      // ends with "tanh^(-1)"
    /d[a-zA-Z]\s*\/\s*d[a-zA-Z]$/,  // derivative patterns like "dy/dx"
    /d\s*\/\s*d[a-zA-Z]$/,          // derivative patterns like "d/dx"
    /d²\s*\/\s*d[a-zA-Z]²$/,        // second derivative patterns
    /∂[a-zA-Z]\s*\/\s*∂[a-zA-Z]$/,  // partial derivatives
    /partial[a-zA-Z]\s*\/\s*partial[a-zA-Z]$/i, // partial derivatives (text)
    /int\s+.*\s+d[a-zA-Z]$/,        // indefinite integral patterns like "int f(x) dx"
    /int\[[^,\]]+,[^\]]+\]\s+.*\s+d[a-zA-Z]$/,  // definite integral like "int[0,1] x dx"
    /∫\s*.*\s+d[a-zA-Z]$/,          // integral with unicode symbol
    /iint$/i,            // double integral
    /iiint$/i           // triple integral
  ];
  
  return immediatePatterns.some(pattern => pattern.test(text));
};